import React, { useEffect, useState } from 'react'

interface Props {
  reason?: string
  estimatedEnd?: string | null
  services?: string[]
  logoUrl?: string | null
  siteName?: string
}

const ModernMaintenancePage: React.FC<Props> = ({ reason, estimatedEnd, logoUrl, siteName }) => {
  const [elapsed, setElapsed] = useState(0)
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(e => e + 1)
      setDots(d => d.length >= 3 ? '.' : d + '.')
    }, 1000)
    return () => clearInterval(t)
  }, [])

  const formatEstimated = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('th-TH', {
        dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Bangkok',
      })
    } catch { return iso }
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Kanit', 'Sarabun', sans-serif",
        padding: '24px',
        color: '#e2e8f0',
      }}
    >
      {/* Tenant Logo */}
      {logoUrl && (
        <div style={{ marginBottom: '28px', animation: 'fadeInDown 0.6s ease forwards', textAlign: 'center' }}>
          <img
            src={logoUrl}
            alt={siteName || 'Logo'}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            style={{
              maxHeight: '72px',
              maxWidth: '220px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 14px rgba(99,102,241,0.45))',
              borderRadius: '8px',
            }}
          />
          {siteName && (
            <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '8px', letterSpacing: '0.03em' }}>
              {siteName}
            </p>
          )}
        </div>
      )}

      {/* Animated gear icon */}
      <div style={{ marginBottom: '28px', position: 'relative' }}>
        <svg
          width="88" height="88" viewBox="0 0 24 24" fill="none"
          style={{ animation: 'spin 6s linear infinite', display: 'block' }}
        >
          <path
            d="M12 15a3 3 0 100-6 3 3 0 000 6z"
            stroke="#818cf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          />
          <path
            d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
            stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
        <span style={{
          position: 'absolute', inset: '-8px', borderRadius: '50%',
          border: '2px solid #6366f1', opacity: 0.4,
          animation: 'pulse 2s ease-in-out infinite',
        }} />
      </div>

      {/* Title */}
      <h1 style={{
        fontSize: 'clamp(22px, 5vw, 34px)',
        fontWeight: 700,
        color: '#c7d2fe',
        marginBottom: '8px',
        textAlign: 'center',
        letterSpacing: '0.01em',
      }}>
        🔧 ปิดปรับปรุงชั่วคราว
      </h1>
      <h2 style={{
        fontSize: 'clamp(14px, 3vw, 20px)',
        fontWeight: 400,
        color: '#94a3b8',
        marginBottom: '28px',
        textAlign: 'center',
      }}>
        Temporarily Under Maintenance
      </h2>

      {/* Card */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(99,102,241,0.3)',
        borderRadius: '16px',
        padding: '32px 40px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: '16px', color: '#cbd5e1', lineHeight: 1.8, marginBottom: '8px' }}>
          ขณะนี้ระบบอยู่ระหว่างการปรับปรุง
        </p>
        <p style={{ fontSize: '14px', color: '#94a3b8', lineHeight: 1.8, marginBottom: '20px' }}>
          Our system is currently undergoing scheduled maintenance.
          <br />We apologize for the inconvenience.
        </p>

        {reason && (
          <div style={{
            background: 'rgba(99,102,241,0.1)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#a5b4fc',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.8,
          }}>
            {reason}
          </div>
        )}

        {estimatedEnd && (
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
            คาดว่าจะเปิดให้บริการอีกครั้ง:{' '}
            <strong style={{ color: '#818cf8' }}>
              {formatEstimated(estimatedEnd)}
            </strong>
          </p>
        )}

        <p style={{ fontSize: '14px', color: '#475569', marginTop: '8px' }}>
          กรุณารอสักครู่{dots}
          <span style={{ marginLeft: '12px', fontSize: '12px', color: '#334155' }}>
            ({elapsed}s)
          </span>
        </p>
      </div>

      <p style={{ marginTop: '28px', fontSize: '13px', color: '#334155', textAlign: 'center' }}>
        ขออภัยในความไม่สะดวก · We appreciate your patience
      </p>

      <style>{`
        @keyframes spin        { to { transform: rotate(360deg) } }
        @keyframes pulse       { 0%,100% { transform: scale(1); opacity: .4 } 50% { transform: scale(1.15); opacity: .15 } }
        @keyframes fadeInDown  { from { opacity: 0; transform: translateY(-14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  )
}

export default ModernMaintenancePage
